import React, { useState, useRef } from 'react';
import { api } from '../../services/api';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const ModuleYUpload: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [file1Text, setFile1Text] = useState('');
  const [file1Name, setFile1Name] = useState('');
  const [file2Text, setFile2Text] = useState('');
  const [file2Name, setFile2Name] = useState('');

  const [monto, setMonto] = useState('');
  const [semanas, setSemanas] = useState('');
  const [moneda, setMoneda] = useState('MXN');
  const [monedaOtra, setMonedaOtra] = useState('');

  const fileInputRef1 = useRef<HTMLInputElement>(null);
  const fileInputRef2 = useRef<HTMLInputElement>(null);

  const [modoSimulacion, setModoSimulacion] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, fileNumber: 1 | 2) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('context_files', file);

    try {
      const res = await api.post(`/strategies/${strategyId}/modules/11/upload_context`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (fileNumber === 1) {
        setFile1Text(res.data.text);
        setFile1Name(file.name);
      } else {
        setFile2Text(res.data.text);
        setFile2Name(file.name);
      }
    } catch (err) {
      console.error(err);
      alert('Error al subir y procesar el archivo. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!modoSimulacion) {
      if (!file1Text) {
        return alert('Falta subir la información de media mix de inversión de la categoría.\nEste dato es necesario para entender cómo se distribuye la inversión por punto de contacto.');
      }
      if (!file2Text) {
        return alert('Falta subir la información de volumen de inversión por marca competidora.\nEste dato es necesario para comparar el presupuesto disponible de la marca contra el nivel de inversión de la categoría.');
      }
    }
    if (!monto || !semanas || (!moneda && !monedaOtra)) {
      return alert('Falta completar el presupuesto disponible de la marca.\nPara continuar, introduce monto total, semanas de inversión y moneda.');
    }

    const numericMonto = parseInt(monto.replace(/[^0-9]/g, ''), 10);
    if (isNaN(numericMonto) || numericMonto <= 0) {
      return alert('El monto de inversión disponible debe ser una cifra numérica entera mayor que 0.\nEscríbelo solo con números sin comas ni puntos.');
    }

    const numericSemanas = parseInt(semanas.replace(/[^0-9]/g, ''), 10);
    if (isNaN(numericSemanas) || numericSemanas <= 0) {
      return alert('El número de semanas de inversión debe ser un entero mayor que 0.');
    }

    const finalMoneda = moneda === 'Otra' ? monedaOtra.trim().toUpperCase() : moneda;
    if (!finalMoneda) {
      return alert('Selecciona la moneda en la que está expresado el presupuesto disponible antes de continuar.');
    }

    setLoading(true);
    try {
      const payload = {
        inputs: {
          media_mix_categoria_texto: modoSimulacion && !file1Text ? 'Modo Simulación: Datos de referencia estimativos de la categoría.' : file1Text,
          volumen_competidores_texto: modoSimulacion && !file2Text ? 'Modo Simulación: Datos estimativos de competencia.' : file2Text,
          presupuesto_monto: numericMonto,
          presupuesto_semanas: numericSemanas,
          presupuesto_moneda: finalMoneda,
          modo_simulacion: modoSimulacion
        }
      };

      const res = await api.post(`/strategies/${strategyId}/modules/11/process`, payload);
      if (res.status === 200) {
        onComplete();
      }
    } catch (err: any) {
      console.error(err);
      const errorMsg = err.response?.data?.error || err.message || 'Error desconocido';
      alert(`Error al procesar la información de Módulo Y: ${errorMsg}\n\nPor favor, verifica los datos e inténtalo de nuevo.`);
    } finally {
      setLoading(false);
    }
  };

  const monedas = [
    'MXN', 'USD', 'EUR', 'COP', 'PEN', 'CLP', 'ARS', 'BRL', 'CRC', 'PAB', 'GTQ', 'UYU', 'GBP', 'CAD', 'Otra'
  ];

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo Y: Subida de Información</h2>
      <p style={styles.description}>
        Por favor, carga los datos competitivos de inversión para que el asistente pueda realizar el diagnóstico.
      </p>

      {/* Espacio 1 */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          Espacio 1: Media mix de inversión de la categoría en un periodo determinado, excluyendo a la marca analizada
        </h3>
        <p style={styles.cardSubText}>
          Formatos soportados: Word, Excel, PDF, Imagen, etc.
        </p>
        <div style={styles.uploadBox}>
          <input 
            type="file" 
            ref={fileInputRef1} 
            style={{ display: 'none' }}
            onChange={e => handleFileUpload(e, 1)}
            disabled={loading}
          />
          <button style={styles.uploadBtn} onClick={() => fileInputRef1.current?.click()} disabled={loading}>
            <UploadIcon />
            <span>{file1Name ? 'Cambiar archivo' : 'Subir archivo'}</span>
          </button>
          {file1Name && <span style={styles.fileName}>✓ {file1Name}</span>}
        </div>
      </div>

      {/* Espacio 2 */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          Espacio 2: Volumen de inversión por marca competidora dentro de esa categoría y periodo
        </h3>
        <p style={styles.cardSubText}>
          Formatos soportados: Word, Excel, PDF, Imagen, etc.
        </p>
        <div style={styles.uploadBox}>
          <input 
            type="file" 
            ref={fileInputRef2} 
            style={{ display: 'none' }}
            onChange={e => handleFileUpload(e, 2)}
            disabled={loading}
          />
          <button style={styles.uploadBtn} onClick={() => fileInputRef2.current?.click()} disabled={loading}>
            <UploadIcon />
            <span>{file2Name ? 'Cambiar archivo' : 'Subir archivo'}</span>
          </button>
          {file2Name && <span style={styles.fileName}>✓ {file2Name}</span>}
        </div>
      </div>

      {/* Espacio 3 */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Espacio 3: Presupuesto disponible de la marca para ese mismo periodo</h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Monto total de inversión disponible (Solo enteros)</label>
            <input 
              style={styles.input} 
              type="text" 
              value={monto} 
              onChange={e => setMonto(e.target.value.replace(/[^0-9]/g, ''))} 
              placeholder="Ej: 5000000"
              disabled={loading}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Semanas de inversión de la campaña</label>
            <input 
              style={styles.input} 
              type="text" 
              value={semanas} 
              onChange={e => setSemanas(e.target.value.replace(/[^0-9]/g, ''))} 
              placeholder="Ej: 12"
              disabled={loading}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Moneda</label>
            <select 
              style={styles.select} 
              value={moneda} 
              onChange={e => setMoneda(e.target.value)}
              disabled={loading}
            >
              {monedas.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {moneda === 'Otra' && (
              <input 
                style={{ ...styles.input, marginTop: '8px' }} 
                value={monedaOtra} 
                onChange={e => setMonedaOtra(e.target.value)} 
                placeholder="Código (Ej: MXN)"
                disabled={loading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modo Simulación Card */}
      <div style={{
        ...styles.card,
        background: modoSimulacion ? 'rgba(59, 130, 246, 0.05)' : 'var(--bg-card)',
        borderColor: modoSimulacion ? 'var(--accent)' : 'var(--border)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={styles.cardTitle}>⚙️ Modo Simulación</h3>
            <p style={styles.cardSubText}>
              Si no cuentas con los datos reales de la categoría, activa este modo para generar un diagnóstico preliminar/estimativo.
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
            <input 
              type="checkbox"
              checked={modoSimulacion}
              onChange={e => setModoSimulacion(e.target.checked)}
              disabled={loading}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>{modoSimulacion ? 'Simulación Activada' : 'Activar Simulación'}</span>
          </label>
        </div>
        {modoSimulacion && (
          <div style={{ fontSize: '12px', color: 'var(--warning)', fontStyle: 'italic', marginTop: '4px' }}>
            * El diagnóstico resultante en Módulo Z será marcado formalmente como "No Concluyente / Preliminar".
          </div>
        )}
      </div>

      <div style={{ marginTop: '16px' }}>
        <button 
          style={styles.submitBtn} 
          onClick={handleSubmit} 
          disabled={loading || (!modoSimulacion && (!file1Text || !file2Text)) || !monto || !semanas}
        >
          {loading ? 'Procesando...' : 'Analizar información'}
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '20px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', fontWeight: 600, margin: 0 },
  description: { fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '8px' },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: 'var(--shadow)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  cardTitle: { fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: 0, lineHeight: 1.4 },
  cardSubText: { fontSize: '12px', color: 'var(--text-muted)', margin: 0 },
  uploadBox: { display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' },
  uploadBtn: {
    background: 'transparent',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s'
  },
  fileName: { fontSize: '13px', color: 'var(--success)', fontWeight: 500 },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '8px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' },
  input: {
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    fontSize: '14px',
    background: 'transparent',
    color: 'var(--text-primary)',
    outline: 'none'
  },
  select: {
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    fontSize: '14px',
    background: 'var(--bg-card)',
    color: 'var(--text-primary)',
    outline: 'none'
  },
  submitBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '14px 28px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    boxShadow: 'var(--shadow)',
    transition: 'opacity 0.2s'
  }
};

export default ModuleYUpload;
